import React from 'react';
import classNames from 'classnames';
import { BASE_IMG_URL } from '@images/imageUrls';
import styles from './index.module.scss';
import type { FeedbackProductCategory, FeedbackProductPickerProps } from '../index';
import {
    buildPickerOpenState,
    findProductByValue,
    isProductSearchMode,
    loadProductsByKeyword,
    loadProductsForCategory,
    PRODUCT_SEARCH_DEBOUNCE_MS,
    shouldInitializePicker,
    type ProductPickerLoaderState,
} from '../index';
import { releaseModalBodyScrollLock, syncModalBodyScrollLock } from '@utils/modalScrollLock';

type TransitionState = 'hidden' | 'entering' | 'entered' | 'exiting';

interface PCFeedbackProductPickerState extends ProductPickerLoaderState {
    transitionState: TransitionState;
}

class PCFeedbackProductPicker extends React.Component<FeedbackProductPickerProps, PCFeedbackProductPickerState> {
    state: PCFeedbackProductPickerState = {
        ...buildPickerOpenState([]),
        transitionState: 'hidden',
    };

    requestId = 0;

    searchTimer: ReturnType<typeof setTimeout> | null = null;
    transitionTimer: ReturnType<typeof setTimeout> | null = null;

    componentDidUpdate(prevProps: FeedbackProductPickerProps) {
        syncModalBodyScrollLock(this.props.visible, prevProps.visible);

        if (this.props.visible && !prevProps.visible) {
            if (this.transitionTimer) {
                clearTimeout(this.transitionTimer);
                this.transitionTimer = null;
            }
            this.setState({ transitionState: 'entering' });
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this.setState({ transitionState: 'entered' });
                });
            });
            document.addEventListener('keydown', this.handleKeyDown);
        }

        if (!this.props.visible && prevProps.visible) {
            this.invalidateRequests();
            this.setState({ transitionState: 'exiting' });
            this.transitionTimer = setTimeout(() => {
                this.setState({ transitionState: 'hidden' });
                this.transitionTimer = null;
            }, 200);
            document.removeEventListener('keydown', this.handleKeyDown);
        }

        if (shouldInitializePicker(prevProps, this.props, this.state)) {
            this.initState();
        }
    }

    componentWillUnmount() {
        this.invalidateRequests();
        if (this.transitionTimer) {
            clearTimeout(this.transitionTimer);
        }
        document.removeEventListener('keydown', this.handleKeyDown);
        releaseModalBodyScrollLock(this.props.visible);
    }

    handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            this.props.onClose();
        }
    };

    invalidateRequests = () => {
        this.requestId += 1;
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
    };

    initState = () => {
        this.invalidateRequests();
        const { categories, value } = this.props;
        const nextState = buildPickerOpenState(categories, value);

        this.setState(nextState, () => {
            if (nextState.activeCategoryId) {
                this.loadCategoryProducts(nextState.activeCategoryId);
            }
        });
    };

    loadCategoryProducts = async (categoryId: string) => {
        const requestId = ++this.requestId;
        this.setState({ loading: true });
        try {
            const products = await loadProductsForCategory(this.props.fetchProducts, categoryId);
            if (requestId !== this.requestId) return;
            this.setState({
                products,
                loading: false,
                selectedProduct: findProductByValue(products, this.state.selectedValue),
            });
        } catch (error) {
            if (requestId !== this.requestId) return;
            console.error('获取产品列表失败:', error);
            this.setState({ products: [], loading: false });
        }
    };

    loadGlobalProducts = async (keywords: string) => {
        const requestId = ++this.requestId;
        this.setState({ loading: true });
        try {
            const products = await loadProductsByKeyword(this.props.fetchProducts, keywords);
            if (requestId !== this.requestId) return;
            this.setState({
                products,
                loading: false,
                selectedProduct: findProductByValue(products, this.state.selectedValue),
            });
        } catch (error) {
            if (requestId !== this.requestId) return;
            console.error('搜索机型失败:', error);
            this.setState({ products: [], loading: false });
        }
    };

    handleCategoryChange = (categoryId: string) => {
        this.invalidateRequests();
        this.setState({
            activeCategoryId: categoryId,
            searchValue: '',
            selectedValue: '',
            selectedProduct: null,
        }, () => {
            this.loadCategoryProducts(categoryId);
        });
    };

    handleSearchChange = (value: string) => {
        this.invalidateRequests();
        this.setState({ searchValue: value });

        if (!isProductSearchMode(value)) {
            const { activeCategoryId } = this.state;
            if (activeCategoryId) {
                this.loadCategoryProducts(activeCategoryId);
            } else {
                this.setState({ products: [], loading: false });
            }
            return;
        }

        this.setState({ loading: true });
        this.searchTimer = setTimeout(() => {
            this.searchTimer = null;
            this.loadGlobalProducts(value);
        }, PRODUCT_SEARCH_DEBOUNCE_MS);
    };

    handleSearchClear = () => {
        this.handleSearchChange('');
    };

    handleConfirm = () => {
        const { onConfirm } = this.props;
        const { selectedProduct } = this.state;
        onConfirm(selectedProduct);
    };

    render() {
        const { categories, onClose } = this.props;
        const { activeCategoryId, selectedValue, searchValue, loading, products, transitionState } = this.state;
        const searchMode = isProductSearchMode(searchValue);
        const isVisible = transitionState === 'entered';

        if (transitionState === 'hidden') return null;

        return (
            <div
                className={classNames(styles.mask, isVisible && styles.maskVisible)}
                onClick={onClose}
            >
                <div
                    className={classNames(styles.modal, isVisible && styles.modalVisible)}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={styles.header}>
                        <div className={styles.title}>手机型号</div>
                        <img
                            src={`${BASE_IMG_URL}/feedback/icon4.png`}
                            alt="close"
                            className={styles.closeBtn}
                            onClick={onClose}
                        />
                    </div>

                    <div className={styles.searchBox}>
                        <img
                            src={`${BASE_IMG_URL}/common/search.png`}
                            alt="search"
                            className={styles.searchIcon}
                        />
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="搜索机型"
                            value={searchValue}
                            onChange={(e) => this.handleSearchChange(e.target.value)}
                        />
                        {!!searchValue && (
                            <button
                                type="button"
                                className={styles.clearBtn}
                                onClick={this.handleSearchClear}
                                aria-label="清空"
                            />
                        )}
                    </div>

                    <div className={classNames(styles.body, searchMode && styles.bodySearch)}>
                        {!searchMode && (
                            <div className={styles.categoryList} data-body-scroll-lock-scrollable>
                                {categories.map((category: FeedbackProductCategory) => (
                                    <div
                                        key={category.id}
                                        className={classNames(
                                            styles.categoryItem,
                                            String(category.id) === String(activeCategoryId) && styles.categoryItemActive,
                                        )}
                                        onClick={() => this.handleCategoryChange(String(category.id))}
                                    >
                                        {category.name}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div
                            className={classNames(styles.productList, searchMode && styles.productListSearch)}
                            data-body-scroll-lock-scrollable
                        >
                            {loading && <div className={styles.emptyText}>加载中...</div>}
                            {!loading && products.map((product) => {
                                const checked = selectedValue === product.value;
                                return (
                                    <div
                                        key={product.value}
                                        className={classNames(styles.productItem, checked && styles.productItemActive)}
                                        onClick={() => this.setState({
                                            selectedValue: product.value,
                                            selectedProduct: product,
                                        })}
                                    >
                                        <span className={styles.productName}>{product.name}</span>
                                        <span className={classNames(styles.radio, checked && styles.radioChecked)} />
                                    </div>
                                );
                            })}
                            {!loading && !products.length && (
                                <div className={styles.emptyText}>暂无匹配机型</div>
                            )}
                        </div>
                    </div>

                    <div className={classNames(styles.footer, !!searchValue ? styles.searchFooter : '')}>
                        <button type="button" className={styles.cancelBtn} onClick={onClose}>取消</button>
                        <button type="button" className={styles.confirmBtn} onClick={this.handleConfirm}>确定</button>
                    </div>
                </div>
            </div>
        );
    }
}

export default PCFeedbackProductPicker;
